<script setup>

import notification from "@/utils/plugins/NotificationService";
import { useUiStore } from "@/app/stores/uiStore";
import { computed, onMounted, ref } from "vue";
import { filterByNormalizedText } from "@/utils/textSearch";

// Icon
import IconBlueBox from '@/components/icons/IconBlueBox.vue'


// Components
import HeaderTitle from "@/components/layout/HeaderTitle.vue";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import MaterialDialog from "@/components/shared/Material/MaterialDialog.vue";
import MaterialDuplicateDialog from "@/components/shared/Material/MaterialDuplicateDialog.vue";

// Services
import MaterialService from '@/api/services/material.service';
import MaterialTypeService from '@/api/services/materialType.service';
import EmployeeService from '@/api/services/employee.service';

const uiStore = useUiStore();

const materialserialTag = ref()
const materialStatus = ref()
const materialType = ref()
const materialOwner = ref()
const materialEmployee = ref()
const acquisitionType = ref()
const optionsStatus = ref([
  {
    name: "Disponible",
    value: "available",
  },
  {
    name: "Asignado",
    value: "assigned"
  },
  {
    name: "Retirado",
    value: "decommissioned"
  },
]);
const ownerTypes = ref([
  {
    name: "Sistemas",
    value: "systems"
  },
  {
    name: "Empleado",
    value: "employee"
  },

]);
const acquisitionTypes = ref([
  {
    name: "Compra",
    value: "purchased"
  },
  {
    name: "Renting",
    value: "renting"
  },
]);
const optionsTypes = ref([]);
const employees = ref([]);
const employeeSearchQuery = ref('');
const filteredEmployees = computed(() => {
  return filterByNormalizedText(employees.value, employeeSearchQuery.value, employee => employee.fullName);
})

const showMaterialDialog = ref(false);
const showModalDecommission = ref(false)
const showDuplicateDialog = ref(false)

const filteredMaterial = ref([]);
const selectedMaterial = ref();
const duplicateSourceMaterial = ref();
const page = ref(1);
const itemsPerPage = ref(30);
const totalResults = ref(0);
const params = ref({})
const statusSummary = ref(null);

const decommissionDate = ref();
const decommissionReason = ref();
const decommissionErrors = ref({
  date: false,
  reason: false
});

onMounted(() => {
  getMaterials();
  getMaterialsTypes();
  getEmployees();
})

const setSearchParam = (key, value) => {
  if (value === null || value === undefined || value === '') {
    delete params.value[key];
    return;
  }

  params.value[key] = value;
}

const search = () => {
  setSearchParam('identifier', materialserialTag.value);
  setSearchParam('status', materialStatus.value);
  setSearchParam('type.name', materialType.value);
  setSearchParam('ownerType', materialOwner.value);
  setSearchParam('currentAssignment.employee.id', materialEmployee.value?.id);
  setSearchParam('acquisitionType', acquisitionType.value);

  page.value = 1;
  itemsPerPage.value = 30;

  getMaterials();
}

const searchByOwnerType = () => {
  if (materialOwner.value !== 'employee') {
    materialEmployee.value = null;
  }

  search();
}

const searchByEmployee = () => {
  if (materialEmployee.value) {
    materialOwner.value = 'employee';
  }

  search();
}

const filterEmployeesByName = (query) => {
  employeeSearchQuery.value = query;
}

const closeDialogMaterialDelivery = (reset = false) => {
  showMaterialDialog.value = false;
  selectedMaterial.value = null;
  decommissionDate.value = null;
  decommissionReason.value = null;
  if (reset) {
    getMaterials();
  }
}
// eslint-disable-next-line no-unused-vars
const openDialogMaterialDelivery = (material = null) => {
  selectedMaterial.value = material;
  showMaterialDialog.value = true;
}

const openDialogDuplicate = (material) => {
  duplicateSourceMaterial.value = material;
  showDuplicateDialog.value = true;
}

const switchToDuplicateDialog = (material) => {
  showMaterialDialog.value = false;
  openDialogDuplicate(material);
}

const closeDialogDuplicate = (reset = false) => {
  showDuplicateDialog.value = false;
  duplicateSourceMaterial.value = null;
  if (reset) {
    getMaterials();
  }
}

const getMaterials = () => {
  params.value.page = page.value;
  params.value.itemsPerPage = itemsPerPage.value;

  const summaryParams = { ...params.value };
  delete summaryParams.page;
  delete summaryParams.itemsPerPage;

  uiStore.startLoading();
  Promise.all([
    MaterialService.getMaterials(params.value),
    MaterialService.getStatusSummary(summaryParams),
  ])
    .then(([listRes, summaryRes]) => {
      if (listRes && listRes.status === 200) {
        filteredMaterial.value = listRes.data['hydra:member']
        totalResults.value = listRes.data['hydra:totalItems'];
      }
      if (summaryRes) {
        statusSummary.value = summaryRes.data;
      }
    })
    .finally(() => {
      uiStore.stopLoading();
    })
}

const getEmployees = () => {
  const params = {
    active: true,
    'order[name]': 'asc',
    page: 1,
    itemsPerPage: 999999,
  };

  uiStore.startLoading();
  EmployeeService.getEmployees(params)
    .then((res) => {
      if (res && res.status === 200) {
        employees.value = res.data['hydra:member'];
      }
    })
    .finally(() => {
      uiStore.stopLoading();
    })
}

const getMaterialsTypes = () => {
  // eslint-disable-next-line no-unused-vars
  const param = {
    'page': 1,
    'itemsPerPage': 99999,
  }
  uiStore.startLoading();
  MaterialTypeService.getMaterialTypes(param)
    .then((res) => {
      if (res && res.status === 200) {
        optionsTypes.value = res.data['hydra:member']
      }
    })
    .finally(() => {
      uiStore.stopLoading();
    })
}


const openDialogDecommission = (material) => {
  showModalDecommission.value = true;
  selectedMaterial.value = material;
}

const decommissionMaterialSelected = (selected) => {
  let params = {
    decommissionedAt: decommissionDate.value,
    decommissionReason: decommissionReason.value,
  }
  MaterialService.decommissionMaterial(selected.id, params)
    .then((res) => {
      if (res && res.status && (res.status === 201 || res.status === 200)) {
        notification.addNotification({
          type: 'success',
          message: 'Se ha dado de baja el material correctamente',
          duration: 6000
        })
      }
    })
}

const getStatusName = (name) => {
  const found = optionsStatus.value.find(option => option.value === name);
  return found ? found.name : null;
}
// eslint-disable-next-line no-unused-vars
const getTypeName = (name) => {
  const found = optionsTypes.value.find(option => option.value === name);
  return found ? found.name : null;
}

const getCurrentAssignmentName = (material) => {
  if (material && material.currentAssignment) {
    if (material.currentAssignment.responsible === 'systems') return 'Sistemas'
    if (material.currentAssignment.employee) {
      return material.currentAssignment.employee.fullName
    }
  }

  return '';
}

const returnStatusClass = (material) => {
  switch (material.status) {
    case "available":
      return "status-available";
    case "assigned":
      return "status-assigned";
    case "decommissioned":
      return "status-decommissioned";
    default:
      return "";
  }
};

const returnRentingClass = (material) => {
  switch (getRentingStatus(material)) {
    case "Renting OK":
      return "status-available";
    case "Renting < 1 mes":
      return "status-assigned";
    case "Renting finalizado":
    case "Renting < 7 días":
      return "status-decommissioned";
    case "Renting < 2 meses":
      return "status-temporary";
    default:
      return "";
  }
}

const getRentingStatus = (material) => {
  if (!material?.rentingEndDate) return "";

  if (material?.rentingAlert7DaysNotifiedAt) {
    return "Renting < 7 días"
  }
  if (material?.rentingAlert1MonthNotifiedAt) {
    return "Renting < 1 mes"
  }
  if (material?.rentingAlert2MonthsNotifiedAt) {
    return "Renting < 2 meses"
  }

  const end = new Date(material.rentingEndDate);
  const todaySameHour = new Date();
  todaySameHour.setHours(
    end.getHours(),
    end.getMinutes(),
    end.getSeconds(),
    end.getMilliseconds()
  );

  const diffMs = end - todaySameHour;
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffDays < 0) {
    return "Renting finalizado";
  }

  if (diffDays <= 60) {
    return "Renting < 2 meses";
  }

  return "Renting OK";
}


const closeUnsubscribeModal = (reset = false) => {
  selectedMaterial.value = null;
  decommissionDate.value = null;
  decommissionReason.value = "";
  decommissionErrors.value = { date: false, reason: false };
  showModalDecommission.value = false;
  if (reset) {
    getMaterials();
  }
};

const confirmDecommission = () => {
  // Reset errors
  decommissionErrors.value.date = !decommissionDate.value;
  decommissionErrors.value.reason = !decommissionReason.value.trim();

  if (!decommissionDate.value || !decommissionReason.value.trim()) {
    return;
  }

  const payload = {
    ...selectedMaterial.value,
    decommissionDate: decommissionDate.value,
    decommissionReason: decommissionReason.value
  };

  decommissionMaterialSelected(payload);

  closeUnsubscribeModal(true);
};

const disableFutureDates = (date) => {
  // today sin horas (solo fecha)
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  return date.getTime() > today.getTime();
};

</script>

<template>
  <div>
    <header-title title="Sistemas" />
    <div class="d-flex flex-row justify-content-start">
      <IconBlueBox class="iconBlueBox" />
      <div class="title-text pl-4">
        Material
      </div>
    </div>
    <!-- Barra de busqueda -->
    <el-row>
      <el-card class="card-search w-100 wback" body-style="padding:0">
        <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-4 material-search-row">
          <div class="col">
            <div class="form-item mt-4 w-100 label-common">
              <label for="domain-name" class="w-100">Núm. Serie/Nombre/ENS</label>
              <el-input id="title" v-model="materialserialTag" class="search-input" placeholder="Núm. Serie/Nombre/ENS"
                clearable @change="search" />
            </div>
          </div>
          <div class="col">
            <div class="form-item mt-4 w-100 label-common">
              <label for="owner-type" class="w-100">Tipo de dueño</label>
              <el-select id="owner-type" v-model="materialOwner" filterable clearable placeholder="TODOS"
                @change="searchByOwnerType">
                <el-option v-for="item in ownerTypes" :key="item.value" :label="item.name" :value="item.value" />
              </el-select>
            </div>
          </div>
          <div class="col">
            <div class="form-item mt-4 w-100 label-common">
              <label for="material-employee" class="w-100">Empleado</label>
              <el-select id="material-employee" v-model="materialEmployee" filterable clearable placeholder="TODOS"
                :value-key="'@id'" :filter-method="filterEmployeesByName" @change="searchByEmployee">
                <el-option v-for="employee in filteredEmployees" :key="employee.id" :label="employee.fullName"
                  :value="employee" />
              </el-select>
            </div>
          </div>
          <div class="col">
            <div class="form-item mt-4 w-100 label-common">
              <label for="material-status" class="w-100">Estado de Material</label>
              <el-select id="material-status" v-model="materialStatus" filterable clearable placeholder="TODOS"
                @change="search">
                <el-option v-for="item in optionsStatus" :key="item.value" :label="item.name" :value="item.value" />
              </el-select>
            </div>
          </div>
          <div class="col">
            <div class="form-item mt-4 w-100 label-common">
              <label for="material-type" class="w-100">Tipo de Material</label>
              <el-select id="material-type" v-model="materialType" filterable clearable placeholder="TODOS"
                @change="search">
                <el-option v-for="item in optionsTypes" :key="item['@id']" :label="item.name" :value="item.name" />
              </el-select>
            </div>
          </div>
          <div class="col">
            <div class="form-item mt-4 w-100 label-common">
              <label for="acquisition-type" class="w-100">Tipo de adquisición</label>
              <el-select id="acquisition-type" v-model="acquisitionType" filterable clearable placeholder="TODOS"
                @change="search">
                <el-option v-for="item in acquisitionTypes" :key="item.value" :label="item.name" :value="item.value" />
              </el-select>
            </div>
          </div>
          <div class="col material-search-actions">
            <div class="btn-group-responsive">
              <button class="btn btn_red btn__new" @click="openDialogMaterialDelivery(null)">
                Nuevo
              </button>
              <button class="btn btn_yellow btn-search btn__search" @click="search()">
                Buscar
              </button>
            </div>
          </div>
        </div>
      </el-card>
    </el-row>

    <section v-if="materialType && statusSummary" class="material-kpis">
      <article class="material-kpi-card green">
        <strong>{{ statusSummary.availableCount }}</strong>
        <span>Disponibles</span>
      </article>
      <article class="material-kpi-card blue">
        <strong>{{ statusSummary.assignedCount }}</strong>
        <span>Asignados</span>
      </article>
      <article class="material-kpi-card grey">
        <strong>{{ statusSummary.decommissionedCount }}</strong>
        <span>Retirados</span>
      </article>
    </section>

    <!-- Tabla ordenador -->
    <div class="table table-material table-not-mobile">
      <div v-if="filteredMaterial && filteredMaterial.length > 0">
        <table>
          <thead>
            <tr class="justify-content-start gap-2">
              <th>
                NÚMERO DE SERIE
              </th>
              <th>
                NOMBRE
              </th>
              <th>
                CÓDIGO INTERNO
              </th>
              <th>
                MARCA
              </th>
              <th>
                MODELO
              </th>
              <th>
                ESTADO
              </th>
              <th>
                ASIGNADO A
              </th>
              <th>
                ADQUISICIÓN
              </th>
              <th>
                TIPO
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="material in filteredMaterial" :key="material.id">
              <td @click="openDialogMaterialDelivery(material)" class="pointer">
                {{ material.serialTag ? material.serialTag : '' }}
              </td>
              <td @click="openDialogMaterialDelivery(material)" class="pointer">
                {{ material.name ? material.name : '' }}
              </td>
              <td @click="openDialogMaterialDelivery(material)" class="pointer">
                {{ material.internalCode ? material.internalCode : '' }}
              </td>
              <td @click="openDialogMaterialDelivery(material)" class="pointer">
                {{ material.brand ? material.brand : '' }}
              </td>
              <td @click="openDialogMaterialDelivery(material)" class="pointer">
                {{ material.model ? material.model : '' }}
              </td>
              <td @click="openDialogMaterialDelivery(material)" class="pointer justify-content-center">
                <span :class="returnStatusClass(material)">
                  {{ material.status ? getStatusName(material.status) : '' }}
                </span>
              </td>
              <td @click="openDialogMaterialDelivery(material)" class="pointer">
                {{ getCurrentAssignmentName(material) }}
              </td>
              <td @click="openDialogMaterialDelivery(material)" class="pointer">
                <div class="justify-content-center align-content-center" style="text-align: center;">
                  <div class="col-md-12">
                    {{ material.acquisitionType ? material.acquisitionType === 'renting' ? 'Renting' : 'Compra' : '' }}
                  </div>
                  <div class="col-md-12">
                    <span :class="returnRentingClass(material)">
                      {{ material.status ? getRentingStatus(material) : '' }}
                    </span>
                  </div>
                </div>
              </td>
              <td @click="openDialogMaterialDelivery(material)" class="pointer">
                {{ material.type ? material.type.name : '' }}
              </td>
              <td>
                <div class="buttons d-inline-flex justify-content-end">
                  <div class="pointer" v-if="material.status === 'available'">
                    <font-awesome-layers class="fa-2x" style="margin-left: 5px" title="Dar de baja"
                      @click="openDialogDecommission(material)">
                      <font-awesome-icon icon="circle" style="color:red" />
                      <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                    </font-awesome-layers>
                  </div>
                  <div class="pointer">
                    <font-awesome-layers class="fa-2x" style="margin-left: 5px" title="Duplicar"
                      @click="openDialogDuplicate(material)">
                      <font-awesome-icon icon="circle" style="color: #107DDE" />
                      <font-awesome-icon icon="clone" class="fa-inverse" transform="shrink-7" />
                    </font-awesome-layers>
                  </div>
                  <div class="pointer">
                    <font-awesome-layers class="fa-2x" style="margin-left: 5px" title="Editar"
                      @click="openDialogMaterialDelivery(material)">
                      <font-awesome-icon icon="circle" style="color: #f7d20e" />
                      <font-awesome-icon icon="plus" class="fa-inverse" transform="shrink-6" />
                    </font-awesome-layers>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <SharedElementPagination class="pagination" v-model:current-page="page" :total="totalResults"
          v-model:page-size="itemsPerPage" style="padding: 0px 0px 20px 0px;" @change="page = $event; getMaterials()"
          show-always />
      </div>
      <div v-else>
        <h5>No se han encontrado material según los filtros especificados...</h5>
      </div>
    </div>
    <!-- Tabla movil -->
    <div class="table-mobile">
      <div v-if="filteredMaterial && filteredMaterial.length > 0">
        <div v-for="material in filteredMaterial" :key="material.id">
          <el-row class="mt-4">
            <el-card class="card-material-item">
              <div class="material-mobile-card__delete">
                <font-awesome-layers class="fa-2x pointer" title="Duplicar" style="margin-right: 8px;"
                  @click.stop="openDialogDuplicate(material)">
                  <font-awesome-icon icon="circle" style="color: #107DDE" />
                  <font-awesome-icon icon="clone" class="fa-inverse" transform="shrink-7" />
                </font-awesome-layers>
                <font-awesome-layers class="fa-2x pointer" title="Dar de baja" v-if="material.status === 'available'"
                  @click.stop="openDialogDecommission(material)">
                  <font-awesome-icon icon="circle" style="color:red" />
                  <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                </font-awesome-layers>
              </div>
              <div class="row d-flex pointer" @click="openDialogMaterialDelivery(material)">
                <div class="el-col-xs-24" v-if="material.serialTag">
                  <span class="material-item-span">Número de serie: </span> {{ material.serialTag }}
                </div>
                <div class="el-col-xs-24" v-if="material && material.name">
                  <span class="material-item-span">Nombre: </span>{{ material.name }}
                </div>
                <div class="el-col-xs-24" v-if="material && material.internalCode">
                  <span class="material-item-span">Código interno: </span>{{ material.internalCode }}
                </div>
                <div class="el-col-xs-24" v-if="material && material.brand">
                  <span class="material-item-span">Marca: </span> {{ material.brand }}
                </div>
                <div class="el-col-xs-24" v-if="material && material.model">
                  <span class="material-item-span">Modelo: </span> {{ material.model }}
                </div>
                <div class="el-col-xs-24 mb-1" v-if="getCurrentAssignmentName(material) != ''">
                  <span class="material-item-span">Asignado a: </span>{{ getCurrentAssignmentName(material) }}
                </div>
                <div class="el-col-xs-24 mb-1 justify-content-between d-flex"
                  :class="getRentingStatus(material) != '' ? 'el-col-xs-24' : 'el-col-xs-12'">
                  <span class="material-item-span" :class="getRentingStatus(material) != '' ? 'mt-1' : ''">
                    {{ material.acquisitionType ? material.acquisitionType === 'renting' ? 'Renting' : 'Compra' : '' }}
                  </span>
                  <span :class="returnRentingClass(material)" style="margin-top: 0px !important; text-align: end;"
                    v-if="getRentingStatus(material) != ''">
                    {{ material.status ? getRentingStatus(material) : '' }}
                  </span>
                </div>

                <div class="el-col-xs-12 mt-1" v-if="material && material.type && material.type.name">
                  <span class="material-item-span">Tipo: </span>{{ material.type.name }}
                </div>
                <div class="el-col-xs-12 mb-2 justify-content-end d-flex" v-if="material && material.status">
                  <span :class="returnStatusClass(material)">
                    {{ getStatusName(material.status) }}
                  </span>
                </div>
              </div>
            </el-card>
          </el-row>
        </div>
        <SharedElementPagination class="pagination" v-model:current-page="page" :total="totalResults"
          v-model:page-size="itemsPerPage" @change="page = $event; getMaterials()" show-always />
      </div>
      <div v-else>
        <h5>No se han encontrado material según los filtros especificados...</h5>
      </div>
    </div>
    <!-- Modal Información Material -->
    <el-dialog class="material-modal" style="border-radius: 20px;" v-model="showMaterialDialog" width="601" :show-close="false" :lock-scroll="true"
      @close="closeDialogMaterialDelivery">
      <MaterialDialog v-if="showMaterialDialog" :material="selectedMaterial" @close-dialog="closeDialogMaterialDelivery"
        @duplicate-material="switchToDuplicateDialog" />
    </el-dialog>
    <!-- Modal Duplicar Material -->
    <el-dialog class="material-modal" style="border-radius: 20px;" v-model="showDuplicateDialog" width="801" :show-close="false" :lock-scroll="true"
      @close="closeDialogDuplicate">
      <MaterialDuplicateDialog v-if="showDuplicateDialog" :material="duplicateSourceMaterial"
        @close-dialog="closeDialogDuplicate" />
    </el-dialog>
    <!-- Modal Eliminación de material -->
    <el-dialog v-model="showModalDecommission" width="601" :show-close="false" :lock-scroll="true"
      class="material-decommission-dialog material-modal" style="border-radius: 20px;" @close="closeUnsubscribeModal">
      <div class="material-decommission-dialog__header">
        <div class="title-text material-decommission-dialog__title">
          Dar de baja material
        </div>
        <img class="material-decommission-dialog__close pointer" src="@/assets/images/aspa_blue_small_entrada.png"
          alt="" @click="closeUnsubscribeModal">
      </div>
      <p class="material-decommission-dialog__message">
        ¿Desea dar de baja este material?
      </p>
      <!-- Info material -->
      <div class="d-flex row" v-if="selectedMaterial">
        <div class="col-6 el-col-xs-24 mb-3">
          <label for="serialNumber" class="w-100">Número de serie</label>
          <el-input id="serialNumber" v-model="selectedMaterial.serialTag" placeholder="Introduce el número de serie"
            disabled />
        </div>
        <div class="col-6 el-col-xs-24 mb-3">
          <label for="brand" class="w-100">Marca</label>
          <el-input id="brand" v-model="selectedMaterial.brand" placeholder="Introduce la marca" disabled />
        </div>
        <div class="col-6 el-col-xs-24 mb-3">
          <label for="model" class="w-100">Modelo</label>
          <el-input id="model" v-model="selectedMaterial.model" placeholder="Introduce el modelo" disabled />
        </div>
        <div class="col-6 el-col-xs-24 mb-3">
          <label for="name" class="w-100">Nombre</label>
          <el-input id="name" v-model="selectedMaterial.name" placeholder="Introduce el nombre" disabled />
        </div>
        <div class="col-6 el-col-xs-24 mb-3">
          <label for="internalCode" class="w-100">Código interno</label>
          <el-input id="internalCode" v-model="selectedMaterial.internalCode" placeholder="Introduce el número de serie"
            disabled />
        </div>
        <div class="col-6 el-col-xs-24 mb-3">
          <label for="materialType" class="w-100">Tipo de material</label>
          <el-select popper-class="material-modal-select" id="materialType" v-model="selectedMaterial.type" filterable clearable placeholder="TODOS" disabled
            :value-key="'@id'">
            <el-option v-for="item in optionsTypes" :key="item['@id']" :label="item.name" :value="item" />
          </el-select>
        </div>
      </div>
      <!-- Fecha -->
      <div class="mb-4">
        <label class="d-block">Fecha de baja:</label>
        <el-date-picker popper-class="material-modal-picker" v-model="decommissionDate" type="date" placeholder="Selecciona fecha" format="DD/MM/YYYY"
          :disabled-date="disableFutureDates" style="width: 100%;" />
        <small v-if="decommissionErrors.date" class="text-danger">
          Debes introducir una fecha
        </small>
      </div>
      <!-- Razón -->
      <div class="mb-3">
        <label class="d-block">Razón de baja:</label>
        <el-input v-model="decommissionReason" placeholder="Motivo de baja" type="textarea" rows="3" />
        <small v-if="decommissionErrors.reason" class="text-danger">
          Debes introducir una razón
        </small>
      </div>
      <div class="material-decommission-dialog__actions">
        <div class="material-decommission-dialog__action">
          <button class="btn btn_yellow material-decommission-dialog__button" @click="closeUnsubscribeModal">
            Cancelar
          </button>
        </div>
        <div class="material-decommission-dialog__action">
          <button class="btn btn_red material-decommission-dialog__button"
            @click="confirmDecommission(selectedMaterial)">
            Aceptar
          </button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.btn__search,
.btn__new {
  width: 200px;
  height: 3em;
}

.material-kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin: 18px 0 22px;
}

.material-kpi-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 82px;
  padding: 14px;
  background: #fff;
  border: 1px solid #e5e7ef;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(31, 41, 55, 0.08);
}

.material-kpi-card strong {
  display: block;
  font-size: 26px;
  line-height: 1.1;
}

.material-kpi-card span {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
  margin-top: 4px;
}

.material-kpi-card.green strong {
  color: #19a974;
}

.material-kpi-card.blue strong {
  color: $font-blue-color;
}

.material-kpi-card.grey strong {
  color: #6b7280;
}

@media (max-width: 767px) {
  .material-kpis {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}


.iconBlueBox {
  width: 30px;
  fill: $bg-blue;
}

.material-decommission-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-top: 2px;
}

.material-decommission-dialog__title {
  color: $font-blue-color;
  line-height: 1.15;
}

.material-decommission-dialog__close {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.material-decommission-dialog__message {
  margin: 18px 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.35;
}

.material-decommission-dialog__actions {
  display: flex;
  gap: 20px;
  margin-top: 24px;
  width: 100%;
}

.material-decommission-dialog__action {
  flex: 1 1 0;
  min-width: 0;
  margin-right: 5px;
}

.material-decommission-dialog__button {
  width: 100%;
}


th {
  white-space: nowrap;
}

.material-search-row {
  row-gap: 0;

  >.col {
    min-width: 0;
  }

  :deep(.el-select) {
    width: 100%;
  }
}

.btn-group-responsive {
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;

    button {
      width: 100% !important;
    }
  }
}

.material-search-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

@media(min-width: 1200px) {
  .material-search-actions {
    width: 50%;
  }
}

@media(min-width: 768px) and (max-width: 1199px) {
  .material-search-actions {
    width: 100%;
  }
}

.status-assigned,
.status-available,
.status-temporary,
.status-decommissioned {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.95rem;
  color: #fff;
  text-align: center;
  text-transform: uppercase;
}

.status-assigned {
  background-color: #f1c40f;
}

.status-available {
  background-color: #27ae60;
}

.status-decommissioned {
  background-color: #E53935;
}

.status-temporary {
  background-color: #107DDE;
}

@media(min-width: 768px) {
  .material-search-actions {
    padding-top: 30px;
  }

  .table-mobile {
    display: none;
  }
}

@media(max-width: 767px) {
  .table-not-mobile {
    display: none;
  }

  .filter-options {
    font-size: 20px;
  }

  .material-search-actions {
    padding-top: 0;
  }

  .card-material-item {
    padding: 10px 0px;
    border-radius: 16px;
    font-size: 15px;

    :deep(.el-card__body) {
      position: relative;
    }

    .material-item-span {
      color: $bg-blue;
      font-weight: 800;
      font-size: 16px;
      line-height: 100%;
      letter-spacing: -5%;

    }
  }

  .material-mobile-card__delete {
    position: absolute;
    top: 10px;
    right: 12px;
    z-index: 1;
  }

  .material-decommission-dialog__title {
    font-size: 26px;
  }

}
</style>
